import { useTranslate } from "@lobby/ocb-intl";
import { Quote, TokenAccounts } from "./types";
import { clsx } from "clsx";
import { Icon, useToggle } from "../../shared";
import { ChangeEvent, PropsWithChildren } from "react";
import { USDC_MINT } from "./const";
import { TokenInfo } from "@solana/spl-token-registry";

interface SwapButtonProps {
  isDisabled: boolean;
  isLoading: boolean;
}

export function SwapButton({
  isLoading,
  isDisabled,
  children,
}: PropsWithChildren<SwapButtonProps>) {
  return (
    <button
      className={clsx(
        "my-4 font-bold relative w-full bg-[#c8f284] text-lg [&:not(:disabled)]:hover:opacity-70 transition-opacity duration-200 text-black/75 p-4 rounded-[0.75rem] disabled:cursor-not-allowed disabled:bg-[#c8f284]/50"
      )}
      type="submit"
      disabled={isDisabled}
    >
      <div className={isLoading ? "invisible" : ""}>{children}</div>

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="absolute animate-spin">
            <Icon name="loading" />
          </span>
        </div>
      )}
    </button>
  );
}

interface QuoteDetailsProps {
  quote: Quote | null;
  inputTokenSymbol: string;
}

export function QuoteDetails({ quote, inputTokenSymbol }: QuoteDetailsProps) {
  const { $t } = useTranslate();

  function calcSwapRate(quote: Quote, inputTokenSymbol: string) {
    return `
          1 ${inputTokenSymbol} ≈
          ${Intl.NumberFormat(undefined, {
            maximumFractionDigits: 2,
          }).format(quote.outAmountUi / quote.inAmountUi)}
          USDC
        `;
  }

  return (
    <div className="bg-[#242738] rounded-8 p-3 flex flex-col gap-1 text-sm">
      {quote ? (
        <>
          <QuoteDetailsRow
            label={$t({ defaultMessage: "Rate" })}
            value={calcSwapRate(quote, inputTokenSymbol)}
          />
          <QuoteDetailsRow
            label={$t({ defaultMessage: "Fee" })}
            value={((quote.platformFee as any)?.feeBps ?? 0) / 1e4 + "%"}
          />
          <QuoteDetailsRow
            label={$t({ defaultMessage: "Slippage" })}
            value={(quote.slippageBps ?? 0) / 1e4 + "%"}
          />
        </>
      ) : (
        <>
          <QuoteDetailsRow label={$t({ defaultMessage: "Rate" })} value="N/A" />
          <QuoteDetailsRow label={$t({ defaultMessage: "Fee" })} value="N/A" />
          <QuoteDetailsRow
            label={$t({ defaultMessage: "Slippage" })}
            value="N/A"
          />
        </>
      )}
    </div>
  );
}

interface CurrentTokenBalanceProps {
  token: TokenAccounts;
  quote: Quote | null;
}

function CurrentTokenBalance({ token, quote }: CurrentTokenBalanceProps) {
  const isNotUSDCToken = token.address !== USDC_MINT;

  function calcTokenValueInUSDC(quote: Quote, amount: number) {
    return (quote.outAmountUi / quote.inAmountUi) * amount;
  }

  return (
    <div className="flex items-center gap-1 text-[#9ca3af]">
      <Icon name="cryptoWallet" className="size-2.5" /> {token.amount}{" "}
      {token.symbol}
      {quote &&
        isNotUSDCToken &&
        ` ≈ ${Intl.NumberFormat(undefined, {
          maximumFractionDigits: 2,
        }).format(calcTokenValueInUSDC(quote, token.amount))} USDC`}
    </div>
  );
}

interface TokenListDropdownToggleButtonProps {
  token: TokenAccounts;
  onClick: VoidFunction;
}

function TokenListDropdownToggleButton({
  token,
  onClick,
}: TokenListDropdownToggleButtonProps) {
  return (
    <div
      onClick={onClick}
      className="flex shrink-0 items-center gap-2 cursor-pointer"
    >
      <img
        src={token.logoURI ?? ""}
        alt={token.symbol}
        className="size-10 object-contain rounded-full"
      />
      <div className="space-y-1">
        <div className="font-medium leading-none flex items-center gap-2">
          {token.symbol}
          <svg viewBox="0 0 256 256" className="size-[0.75rem]">
            <polygon
              fill="currentColor"
              points="79.093,0 48.907,30.187 146.72,128 48.907,225.813 79.093,256 207.093,128"
            />
          </svg>
        </div>
        <div className="text-[#9ca3af] leading-none">{token.name}</div>
      </div>
    </div>
  );
}

interface TokenListDropdownProps {
  tokens: TokenAccounts[];
  selectedTokenAddress?: string | null;
  isOpened: boolean;
  onClick: VoidFunction;
  onTokenSelect: (e: ChangeEvent<HTMLInputElement>) => void;
}

function TokenListDropdown({
  selectedTokenAddress,
  tokens,
  isOpened,
  onClick,
  onTokenSelect,
}: TokenListDropdownProps) {
  return (
    <div
      id="cryptoSwapTerminalTokenListDropdown"
      onClick={onClick}
      className={clsx(
        "rounded-[0.5rem] overflow-hidden absolute -bottom-1 left-0 translate-y-1 bg-[#1a1d29] [box-shadow:inset_0_0_1px_rgba(255_255_255_/_0.5)] [filter:drop-shadow(0.125rem_0.25rem_0.375rem_black)]",
        !isOpened && "hidden"
      )}
    >
      <ul>
        {tokens.map((token, idx) => (
          <li key={idx}>
            <label className="flex items-center gap-2 py-2 px-4 hover:bg-white/10 w-full relative font-medium cursor-pointer">
              <input
                type="radio"
                name="token"
                value={idx}
                checked={selectedTokenAddress === token.address}
                onChange={onTokenSelect}
                className="opacity-0 absolute"
              />
              <img
                src={token.logoURI ?? ""}
                alt={token.symbol}
                className="size-9 object-contain rounded-full"
              />
              <div className="space-y-1 leading-none text-[0.875rem] me-5">
                <div className="">{token.symbol}</div>
                <div className="text-[#9ca3af]">{token.name}</div>
              </div>
              <div className="ms-auto">{token.amount}</div>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface InputTokenCardProps {
  token: TokenAccounts | null;
  quote: Quote | null;
  tokens: TokenAccounts[];
  onTokenSelect: (e: ChangeEvent<HTMLInputElement>) => void;
}

export function InputTokenCard({
  token,
  quote,
  tokens,
  onTokenSelect,
}: InputTokenCardProps) {
  const [isTokenSelectorOpened, toggleTokenSelector, setIsTokenSelectorOpened] =
    useToggle(false);

  function handleTokenDropdownToggleClick() {
    const dropdown = document.getElementById(
      "cryptoSwapTerminalTokenListDropdown"
    );
    if (dropdown) {
      toggleTokenSelector();
    }
  }

  return (
    <div className="bg-[#242738] rounded-[0.75rem] p-4 flex flex-col gap-2">
      <div className="flex justify-between items-center font-medium text-sm">
        <div className="text-[#9ca3af]">From</div>
        {token && <CurrentTokenBalance token={token} quote={quote} />}
      </div>

      <div className="flex items-center justify-between gap-3 relative">
        {token && (
          <TokenListDropdownToggleButton
            token={token}
            onClick={handleTokenDropdownToggleClick}
          />
        )}

        <div
          className={clsx(
            "cursor-not-allowed text-right text-xl font-bold",
            !quote?.inAmountUi && "text-[#6b7280]"
          )}
        >
          {Intl.NumberFormat(undefined, {
            maximumFractionDigits: token?.decimals || 2,
          }).format(quote?.inAmountUi || 0)}
        </div>

        <TokenListDropdown
          tokens={tokens}
          selectedTokenAddress={token?.address}
          isOpened={isTokenSelectorOpened}
          onClick={() => setIsTokenSelectorOpened(false)}
          onTokenSelect={onTokenSelect}
        />
      </div>
    </div>
  );
}

interface OutputTokenCardProps {
  outputToken?: TokenInfo;
  outputAmount: number;
  onOutputValueChange: (value: number) => void;
}

export function OutputTokenCard({
  outputToken,
  outputAmount,
  onOutputValueChange,
}: OutputTokenCardProps) {
  return (
    <div
      className="bg-[#242738] rounded-[0.75rem] p-4 flex flex-col gap-2 focus-within:ring-1 ring-[#c7f28480]"
      onClick={(e) => e.currentTarget.querySelector("input")?.focus()}
    >
      <div className="flex justify-between items-center font-medium text-[#9ca3af] text-sm">
        To
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex shrink-0 items-center gap-2">
          <img
            src={outputToken?.logoURI ?? ""}
            alt={outputToken?.symbol}
            className="size-10 object-contain rounded-full"
          />
          <div className="space-y-1 leading-none">
            <div className="font-medium">{outputToken?.symbol ?? "USDC"}</div>
            <div className="text-[#9ca3af]">
              {outputToken?.name ?? "USD Coin"}
            </div>
          </div>
        </div>
        <div>
          <input
            type="text"
            name="outAmount"
            inputMode="numeric"
            autoComplete="off"
            autoCorrect="off"
            value={outputAmount ? Intl.NumberFormat().format(outputAmount) : ""}
            onChange={(e) => {
              const newValue = e.target.value.replace(/[^0-9.]/g, "");
              onOutputValueChange(Number(newValue));
            }}
            required
            placeholder="0"
            className="w-full text-right text-xl font-bold placeholder:text-[#6b7280] p-0 bg-transparent border-none [outline:none!important]"
          />
        </div>
      </div>
    </div>
  );
}

function QuoteDetailsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[#9ca3af]">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

export function SwapSuccessDialog() {
  const { $t } = useTranslate();

  return (
    <dialog
      className="rounded-[0.75rem] inset-0 p-4 size-full overflow-hidden bg-black"
      id="cryptoSwapTerminalSuccessDialog"
    >
      <div className="size-full flex flex-col items-center text-white">
        <div className="text-[#90c672]">
          <svg
            width="1em"
            height="1em"
            viewBox="0 0 256 256"
            className="text-[4.375rem]"
          >
            <path
              fill="currentColor"
              d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24m45.66 85.66l-56 56a8 8 0 0 1-11.32 0l-24-24a8 8 0 0 1 11.32-11.32L112 148.69l50.34-50.35a8 8 0 0 1 11.32 11.32"
            />
          </svg>
        </div>

        {/* <div className="mt-2 flex flex-col items-center justify-center text-center px-4">
              <p className="font-medium">Swapped 0.005094519 SOL to</p>
              <p className="text-2xl font-medium">0.955707 USDC</p>
            </div> */}
        <div className="mt-2 flex flex-col items-center justify-center text-center px-4">
          <p className="text-2xl font-medium">
            {$t({
              defaultMessage: "Swap successful!",
            })}
          </p>
        </div>

        <button
          className={clsx(
            "mt-4 font-bold relative w-full bg-[#c8f284] text-lg [&:not(:disabled)]:hover:opacity-70 transition-opacity duration-200 text-black/75 p-4 rounded-[0.75rem] disabled:cursor-not-allowed disabled:bg-[#c8f284]/50"
          )}
          type="button"
          onClick={() => {
            const dialog = document.getElementById(
              "cryptoSwapTerminalSuccessDialog"
            );
            if (dialog instanceof HTMLDialogElement) {
              dialog.close();
            }
          }}
        >
          {$t({ defaultMessage: "Swap more" })}
        </button>
      </div>
    </dialog>
  );
}
